import React, { RefObject, useCallback, useEffect, useState } from 'react'
import { useIntl } from 'react-intl'
import styled from 'styled-components'

import {
  DateSectionColumn,
  HalfTimeInput,
} from '../../../containers/TowingOrderPage/EditTowingOrder/TowingOrderDetailsSubFormContents'
import sos from '../../../images/sos.gif'
import {
  FormSubGroup,
  FormSubGroupHeader,
  FormSubGroupHeading,
  FormSubGroupWrapper,
  ImageWrapper,
  Section,
  SectionColumn,
} from '../../layout'
import { DateInput, SelectInput, TextAreaInput, TextInput } from '../../layout/form/input'
import { sosVariables } from '../types'
import { useSosCauseOptions } from '../../../util/useSosCauseOptions'
import { formatMessageSafe } from '../../../util/translateInputOptions'
import { messages } from './messages'

interface SosServiceOrderDetailsFormProps {
  getValue: any
  setValue: any
  sosRef?: RefObject<any>
  scrollToThis?: boolean
}

export const SosServiceOrderDetailsForm: React.FunctionComponent<SosServiceOrderDetailsFormProps> = ({
  getValue,
  setValue,
  sosRef,
  scrollToThis,
}) => {
  const intl = useIntl()
  const formatMessage = formatMessageSafe(intl)
  const [shouldScroll, setShouldScroll] = useState<boolean>(scrollToThis || false)
  const handleScroll = useCallback(() => {
    if (sosRef && sosRef.current) {
      sosRef.current.scrollIntoView()
    }

    setShouldScroll(false)
  }, [sosRef, setShouldScroll])

  useEffect(() => {
    if (shouldScroll) {
      handleScroll()
    }
  }, [shouldScroll, handleScroll])

  const deliverable = getValue('sosServiceOrder.deliverable')
  const { causeOptions, onDeliverableChange } = useSosCauseOptions(deliverable, setValue)
  const deliverables = sosVariables.deliverable

  return (
    <SosFormSubGroupWrapper>
      <SosFormSubGroupHeader>
        <FormSubGroupHeading>
          <ImageWrapper>
            <img src={sos} alt="SOS" width={`95px`} />
          </ImageWrapper>
        </FormSubGroupHeading>
      </SosFormSubGroupHeader>
      <FormSubGroup>
        <Section columns={2} marginMobile="0">
          <DateSectionColumn>
            <DateInput label={formatMessage(messages.jobDate)} name="jobDetails.towingDate" required />
          </DateSectionColumn>
          <DateSectionColumn>
            <HalfTimeInput label={formatMessage(messages.time)} name="jobDetails.towingTime" />
            <HalfTimeInput
              setTimeButton={false}
              label={formatMessage(messages.endTime)}
              name="jobDetails.towingEndTime"
            />
          </DateSectionColumn>
          <SectionColumn />
        </Section>
        <Section columns={2}>
          <SectionColumn>
            <TextInput label={formatMessage(messages.caseNumber)} name="sosServiceOrder.caseNumber" disabled />
          </SectionColumn>
          <SectionColumn>
            <SelectInput
              label={formatMessage(messages.insuranceCompany)}
              name="sosServiceOrder.insuranceCompany"
              options={sosVariables.insuranceCompany}
              required
            />
          </SectionColumn>
        </Section>
        <Section columns={2}>
          <SectionColumn>
            <SelectInput
              label={formatMessage(messages.estimatedServiceType)}
              name="sosServiceOrder.deliverable"
              options={deliverables}
              onChange={value => onDeliverableChange(value)}
              required
            />
          </SectionColumn>
          <SectionColumn>
            <SelectInput
              label={formatMessage(messages.estimatedCause)}
              name="sosServiceOrder.cause"
              options={causeOptions}
              required
            />
          </SectionColumn>
        </Section>

        <Section columns={2}>
          <SectionColumn>
            <TextAreaInput label={formatMessage(messages.additionalInfo)} name="jobDetails.additionalInfo" />
          </SectionColumn>
          <SectionColumn>
            <TextAreaInput label={formatMessage(messages.notes)} name="jobDetails.notes" />
          </SectionColumn>
        </Section>
        <Section columns={2}>
          <SectionColumn>
            <TextAreaInput
              label={formatMessage(messages.situationDescription)}
              name="sosServiceOrder.situationDescription"
              disabled
            />
          </SectionColumn>
          <SectionColumn />
        </Section>
      </FormSubGroup>
    </SosFormSubGroupWrapper>
  )
}

export const SosFormSubGroupHeader = styled(FormSubGroupHeader as any)`
  background-color: ${props => props.theme.colors.sosBackgroundColor};
`
export const SosFormSubGroupWrapper = styled(FormSubGroupWrapper as any)`
  border-color: ${props => props.theme.colors.sosBorderColor};
`
